import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import clsx from 'clsx';
import throttle from 'lodash-es/throttle';
import { useLatestRef } from '@components-shared/src/hooks/useLatestRef';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { TimeSlotNavigator } from '@components/Waveform/TimeSlotSelector/TimeSlotNavigator';
import { paintCanvas } from '@components/Waveform/TimeSlotSelector/paintCanvas';
import { BAR_MARGIN, MAX_PREVIEW_PERC } from '@components/Waveform/constants';
import waveformAvgChunker from '@components/Waveform/waveformAvgChunker';
import { gql, TimeSlotSelectorDocument, useQuery } from '@graphql/reactQuery';
import type { UserNft } from '@hooks/useMultipleNftsForRelease';
import { loadTrack as load, seek, useAudioController } from '@store/audio/AudioController';
import { fadeOut, pause, play, setVolume } from '@store/audio/AudioEngineHTML5';
import { setDisableNextPrev } from '@store/audio/AudioMeta';
import { useAudioPosition } from '@store/audio/AudioPosition';
import { SelectSlot } from './SelectSlot';

const DEFAULT_HEIGHT = 78;

/*#__PURE__*/ gql(/* GraphQL */ `
  query timeSlotSelector($id: UUID!) {
    track(id: $id) {
      id
      duration
      normalizedPeaks
    }
  }
`);

const getNextStep = (currentSlot: number, step: number, total: number, claimed: Set<number>) => {
  let newSlot = currentSlot + step;
  while (claimed.has(newSlot) && newSlot >= 0 && newSlot < total) {
    newSlot += step;
  }
  return newSlot;
};

const getXCoordForScrobbler = (slot: number, slotWidth: number) => {
  return (slot + 0.5) * slotWidth;
};

export function SelectSlotWrapper({
  claimedSlots,
  totalSlots,
  selectedSlot,
  onSlotClicked,
  canOverwrite,
  trackId,
  className,
  selectedNft,
  disableSlotSelection,
  loading,
}: {
  claimedSlots: Set<number>;
  totalSlots: number;
  selectedSlot: number | null;
  onSlotClicked: (slot: number) => void;
  canOverwrite: boolean;
  trackId: string;
  className?: string;
  selectedNft?: UserNft;
  disableSlotSelection?: boolean;
  loading?: boolean;
}) {
  const [containerRef] = useState<React.RefObject<HTMLDivElement>>(createRef());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [waveformWidth, setWaveformWidth] = useState(0);
  const [currentTimestamp, setCurrentTimestamp] = useState(0);
  const [hasInitializedSetSlot, setHasInitializedSetSlot] = useState(false);
  const [selectorBounds, setSelectorBounds] = useState<{ min: number; max: number }>({
    min: 0,
    max: totalSlots - 1,
  });

  const totalSlotWidth = waveformWidth / totalSlots;

  const handleResize = useCallback(() => {
    if (containerRef.current) {
      setWaveformWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef]);

  const { data } = useQuery(TimeSlotSelectorDocument, { variables: { id: trackId }, staleTime: 0 });
  const { normalizedPeaks, duration } = data?.data.track ?? {};

  const { ready, playing, activeTrackId } = useAudioController();

  const { percentComplete } = useAudioPosition();
  const isActiveTrack = trackId === activeTrackId;

  const [hoverXCoord, setHoverXCoord] = useState<number>(0);
  const [clickedXCoord, setClickedXCoord] = useState<number>(0);
  const [playbackRange, setPlaybackRange] = useState<
    { startPerc: number; endPerc: number } | undefined
  >();
  const rangeRef = useLatestRef(playbackRange);
  const playingRef = useLatestRef(playing);

  // each slot occupies 1/totalSlots of the waveform
  // each bar is minimum 1px wide
  const barWidth = Math.max(totalSlotWidth - BAR_MARGIN, 1);

  const claimedBars = useMemo(() => {
    const claimedBars = new Set<number>();
    for (const claimedSlot of claimedSlots) {
      claimedBars.add(claimedSlot);
    }
    return claimedBars;
  }, [claimedSlots]);

  const hoveredBars = useMemo(() => {
    const hoveredBars = new Set<number>();
    if (hoverXCoord === 0 || disableSlotSelection) {
      return hoveredBars;
    }
    const hoveredSlot = Math.floor(hoverXCoord / totalSlotWidth);
    hoveredBars.add(hoveredSlot);
    return hoveredBars;
  }, [disableSlotSelection, hoverXCoord, totalSlotWidth]);

  const clickedBars = useMemo(() => {
    const clickedBars = new Set<number>();
    // show claimed nftSongSlot
    if (clickedXCoord === 0 || loading) {
      return clickedBars;
    } else {
      const clickedSlot = Math.floor(clickedXCoord / totalSlotWidth);
      const selectedNftSlotIsClicked = selectedNft?.songSlot === clickedSlot;

      // make sure clicked slot wasn't already claimed
      if (canOverwrite || selectedNftSlotIsClicked || !claimedSlots.has(clickedSlot)) {
        clickedBars.add(clickedSlot);
      }
    }

    return clickedBars;
  }, [canOverwrite, claimedSlots, clickedXCoord, selectedNft?.songSlot, totalSlotWidth, loading]);

  const chunkedPeaks = useMemo(() => {
    if (!normalizedPeaks) return [];
    const numBars = Math.floor(waveformWidth / (barWidth + BAR_MARGIN));
    return waveformAvgChunker(normalizedPeaks, numBars);
  }, [normalizedPeaks, waveformWidth, barWidth]);

  const setDefaultX = useStableCallback(() => {
    setHoverXCoord(0);
  });

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      // debounced hover coord updates
      requestAnimationFrame(() => {
        if (!canvasRef.current) {
          return;
        }
        setHoverXCoord(e.clientX - canvasRef.current.getBoundingClientRect().left);
      });
    },
    [canvasRef],
  );

  const handleSelector = useStableCallback(async (xCoord: number) => {
    if (!canvasRef.current || disableSlotSelection) {
      return;
    }

    const slot = Math.floor(xCoord / totalSlotWidth);

    if (!isActiveTrack) {
      await load({ trackId });
    }
    await setVolume(1);

    const maxBars = totalSlots;
    const startBar = slot === 0 ? 0 : slot;

    const startPerc = startBar / maxBars;
    const endPerc = Math.min((startBar + 1) / maxBars, MAX_PREVIEW_PERC);

    setPlaybackRange({ startPerc, endPerc });
    setClickedXCoord(xCoord);
    onSlotClicked(slot);

    if (duration) {
      setCurrentTimestamp((slot / totalSlots) * duration);
    }
  });

  const handleNftWithSelectedSlot = useStableCallback(() => {
    const slot = selectedNft?.songSlot;
    if (typeof slot !== 'number' || !canvasRef.current) {
      return;
    }

    const xCoord = getXCoordForScrobbler(slot, totalSlotWidth);

    setClickedXCoord(xCoord);
    onSlotClicked(slot);

    if (duration) {
      const currentTimestamp = (slot / totalSlots) * duration;
      setCurrentTimestamp(currentTimestamp);
    }
  });

  const onSelectorClick: React.MouseEventHandler<HTMLCanvasElement> = async e => {
    if (!canvasRef.current || disableSlotSelection) {
      return;
    }

    const xCoord = e.clientX - canvasRef.current.getBoundingClientRect().left;
    await handleSelector(xCoord);
  };

  const onNavigatorClick = useCallback(
    async (xCoord: number) => {
      await handleSelector(xCoord);
    },
    [handleSelector],
  );

  const handlePrevClick = () => {
    if (disableSlotSelection) {
      return;
    }
    const hasPrev = selectedSlot !== null && selectedSlot - 1 >= 0 && duration;

    if (hasPrev) {
      const newSelectedSlot = getNextStep(selectedSlot, -1, totalSlots, claimedSlots);
      const xCoord = getXCoordForScrobbler(newSelectedSlot, totalSlotWidth);
      onNavigatorClick(xCoord);
    }
  };

  const handleNextClick = () => {
    if (disableSlotSelection) {
      return;
    }
    const hasNext = selectedSlot !== null && selectedSlot + 1 < totalSlots && duration;

    if (hasNext) {
      const newSelectedSlot = getNextStep(selectedSlot, 1, totalSlots, claimedSlots);
      const xCoord = getXCoordForScrobbler(newSelectedSlot, totalSlotWidth);
      onNavigatorClick(xCoord);
    }
  };

  const setFirstAvailableSlot = useStableCallback(() => {
    const unclaimedSlots = Array.from({ length: totalSlots }, (_, i) => i).filter(
      slot => !claimedSlots.has(slot),
    );

    const [first, ...rest] = unclaimedSlots;
    const last = rest.pop();

    if (
      typeof first !== 'undefined' &&
      typeof last !== 'undefined' &&
      first >= 0 &&
      waveformWidth > 0
    ) {
      setSelectorBounds({ min: first, max: last });
      onSlotClicked(first);
      if (duration) {
        const firstTimestamp = (first / totalSlots) * duration;
        setCurrentTimestamp(firstTimestamp);
        setHasInitializedSetSlot(true);
      }

      const xCoord = getXCoordForScrobbler(first, totalSlotWidth);
      setClickedXCoord(xCoord);
    }
  });

  useEffect(() => {
    const throttledHandleResize = throttle(handleResize, 300);
    window.addEventListener('resize', throttledHandleResize);
    throttledHandleResize();
    return () => {
      window.removeEventListener('resize', throttledHandleResize);
      throttledHandleResize.cancel();
    };
  }, [handleResize]);

  useEffect(() => {
    if (
      (selectedSlot !== null && claimedSlots.size <= 0 && waveformWidth > 0) ||
      hasInitializedSetSlot
    ) {
      return;
    }

    setFirstAvailableSlot();
  }, [claimedSlots, waveformWidth, selectedSlot, hasInitializedSetSlot, setFirstAvailableSlot]);

  // deps should be debounced with requestAnimationFrame
  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    paintCanvas({
      canvasRef,
      normalizedPeaks: chunkedPeaks,
      maxBarHeight: DEFAULT_HEIGHT * 1.5,
      barWidth,
      barMargin: BAR_MARGIN,
      claimedBars,
      hoveredBars,
      clickedBars,
      canOverwrite,
    });
  }, [chunkedPeaks, claimedBars, clickedBars, hoveredBars, barWidth, canvasRef, canOverwrite]);

  // apply seek after load
  useEffect(() => {
    if (isActiveTrack && playbackRange && ready && duration) {
      const { startPerc } = playbackRange;
      seek(startPerc * duration);
      if (!playingRef.current) play();
    }
  }, [isActiveTrack, ready, playbackRange, duration, playingRef]);

  useEffect(() => {
    const { endPerc } = rangeRef.current ?? {};
    const currPerc = percentComplete / 100;

    if (endPerc && currPerc >= endPerc && activeTrackId) {
      if (currPerc >= MAX_PREVIEW_PERC) {
        pause();
      } else {
        fadeOut(2.5);
        setPlaybackRange(undefined);
      }
    }
  }, [percentComplete, activeTrackId, rangeRef]);

  useEffect(() => {
    // On mobile the audio player is not visible so pause to be safe here
    pause();
    setDisableNextPrev(true);
    return () => {
      pause();
      setDisableNextPrev(false);
    };
  }, []);

  useEffect(() => {
    if (duration) {
      const nftHasSongSlot = typeof selectedNft?.songSlot === 'number';
      if (nftHasSongSlot) {
        handleNftWithSelectedSlot();
      } else {
        setFirstAvailableSlot();
      }
    }
  }, [handleNftWithSelectedSlot, selectedNft?.songSlot, setFirstAvailableSlot, duration]);

  return (
    <div className={clsx('grid w-full gap-4', className)}>
      <div className="flex items-end justify-between">
        <div className="flex flex-col">
          <div className="w-full text-base-m font-semibold md:text-base-l">
            Select timestamp & comment
          </div>
        </div>
        {!canOverwrite && (
          <TimeSlotNavigator
            selectorBounds={selectorBounds}
            timestamp={currentTimestamp}
            selectedSlot={selectedSlot}
            totalSlots={totalSlots}
            handlePrevClick={handlePrevClick}
            handleNextClick={handleNextClick}
            disableArrows={disableSlotSelection}
            className="!mb-0"
            loading={loading}
          />
        )}
      </div>
      <SelectSlot
        containerRef={containerRef}
        canvasRef={canvasRef}
        height={78}
        width={waveformWidth}
        claimedSlots={claimedSlots}
        totalSlots={totalSlots}
        setDefaultX={setDefaultX}
        handleMouseMove={handleMouseMove}
        onSelectorClick={onSelectorClick}
      />
    </div>
  );
}
